import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Col,
  Form,
  FormControl,
  FormLabel,
  InputGroup,
  Spinner,
} from 'react-bootstrap';
import SweetAlert from 'react-bootstrap-sweetalert';
import useAuth from '../hooks/useAuth';
import useForm from '../hooks/useForm';

function validateEmail(email) {
  const regexp = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return regexp.test(email);
}

function SignUpHook(props) {
  const navigate = useNavigate();
  const { addUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [catchError, setIsCatchError] = useState('');
  const [isAlert, setIsAlert] = useState(false);
  const [isError, setIsError] = useState(false);
  const onSuccess = () => {
    navigate('/verify');
    setIsAlert(false);
  };
  const handleSignup = async (e, data) => {
    if (!validateEmail(data.email)) {
      setIsCatchError('Invalid email address');
      setIsError(true);
      return;
    }
    if (data.phoneNumber.length < 9) {
      setIsCatchError('Phone number should be min 9 digits');
      setIsError(true);
      return;
    }
    if (data.password.length < 6) {
      setIsCatchError('Password should be min 6 characters');
      setIsError(true);
      return;
    }
    if (data.password !== data.confirmPassword) {
      setIsCatchError('Password does not match');
      setIsError(true);
      return;
    }
    try {
      setIsLoading(true);
      await addUser(data);
      setIsAlert(true);
      setIsLoading(false);
    } catch (err) {
      setIsCatchError(err.message);
      setIsError(true);
      setIsLoading(false);
    }
  };
  const validators = {
    memberID: {
      required: {
        value: true,
        message: 'Member ID is required',
      },
    },
    firstName: {
      required: {
        value: true,
        message: 'First Name is required',
      },
    },
    lastName: {
      required: {
        value: true,
        message: 'Last Name is required',
      },
    },
    dateOfBirth: {
      required: {
        value: true,
        message: 'Date of birth is required',
      },
    },
    email: {
      required: {
        value: true,
        message: 'Email is required',
      },
    },
    phoneNumber: {
      required: {
        value: true,
        message: 'Phone number is required',
      },
    },
    confirmPassword: {
      required: {
        value: true,
        message: 'Password is required',
      },
    },
    password: {
      required: {
        value: true,
        message: 'password is required',
      },
    },
  };

  const onConfirm = () => {
    setIsError(false);
  };

  const {
    handleChange,
    handleSubmit,
    errors,
  } = useForm({
    onSubmit: handleSignup,
    validators,
  });

  return (
    <>
      <div>
        <Form className="row g-2" noValidate>
          <Form.Group as={Col} lg="6" controlId="inputMemberID">
            <FormLabel>Member Id</FormLabel>
            <FormControl type="number"
                         value={props.memberID}
                         isInvalid={ errors?.memberID }
                         onChange={handleChange('memberID')}
            />
            <Form.Control.Feedback type="invalid">Member Id is required</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={ Col } lg="6" controlId="inputDateOfBirth">
            <FormLabel>Date Of Birth</FormLabel>
            <FormControl type="date"
                         isInvalid={ errors.dateOfBirth }
                         onChange={handleChange('dateOfBirth')}
            />
            <Form.Control.Feedback type="invalid">
              Date Of Birth is required
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} lg="6" controlId="inputFirstName">
            <FormLabel>First Name</FormLabel>
            <FormControl type="text"
                         isInvalid={ errors.firstName }
                         onChange={handleChange('firstName')}
            />
            <Form.Control.Feedback type="invalid">First name is required</Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={ Col } lg="6" controlId="inputLastName">
            <FormLabel>Last Name</FormLabel>
            <FormControl type="text"
                         isInvalid={ errors.lastName }
                         onChange={handleChange('lastName')}
            />
            <Form.Control.Feedback type="invalid">
              Last name is required
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} lg="6" controlId="inputEmail">
            <FormLabel>Email</FormLabel>
            <FormControl type="email"
                         isInvalid={errors.email}
                         onChange={handleChange('email')}
            />
            <Form.Control.Feedback type="invalid">
              Email is required
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={Col} md="6" controlId="inputPhoneNumber">
            <Form.Label>Phone No</Form.Label>
            <InputGroup hasValidation>
              <InputGroup.Text id="inputGroupPrepend">255</InputGroup.Text>
              <Form.Control type="number"
                            isInvalid={ errors.phoneNumber }
                            aria-describedby="inputGroupPrepend"
                            onChange={handleChange('phoneNumber')}
              />
              <Form.Control.Feedback type="invalid">
                Phone number is required
              </Form.Control.Feedback>
            </InputGroup>
          </Form.Group>
          <Form.Group as={ Col } lg="6" controlId="inputPassword">
            <FormLabel>Password</FormLabel>
            <FormControl type="password"
                         isInvalid={ errors.password }
                         onChange={handleChange('password')}
            />
            <Form.Control.Feedback type="invalid">
              Password is required
            </Form.Control.Feedback>
          </Form.Group>
          <Form.Group as={ Col } lg="6" controlId="inputConfirmPassword">
            <FormLabel>Confirm Password</FormLabel>
            <FormControl type="password"
                         isInvalid={ errors.confirmPassword }
                         onChange={handleChange('confirmPassword')}
            />
            <Form.Control.Feedback type="invalid">
              Confirm Password is required
            </Form.Control.Feedback>
          </Form.Group>
          <Button className="w-100 btn btn-green"
                  type="button"
                  disabled={isLoading}
                  onClick={ handleSubmit }
          >
            <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true"
                     hidden={ !isLoading }/>
            <span className="px-2">Sign up</span>
          </Button>
        </Form>
      </div>
      {isAlert
      && <SweetAlert
        success
        title="Successfully Registered!"
        onConfirm={onSuccess}
      >
        Verification code is sent to your phone and email
      </SweetAlert>
      }
      {isError
      && <SweetAlert
        danger
        title= {catchError}
        onConfirm={onConfirm}
        timeout={10000}
      >
      </SweetAlert>}
    </>
  );
}

export default SignUpHook;
